import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import {
  getMockRestRegistry,
  resolveMockForRestEndpoint,
} from "@redotech/rpc-mock/mock-rest";
import {
  clearMockOverride,
  clearMockRestResponse,
  getCalledRpcs,
  getKnownRpcs,
  getMockDefault,
  getMockOverrides,
  getMockResponse,
  getMockRestResponse,
  isMockModeEnabled,
  resetMockOverrides,
  setMockDefault,
  setMockModeEnabled,
  setMockOverride,
  setMockRestResponse,
} from "@redotech/rpc-mock/mock-storage";
import classNames from "classnames";
import { memo, useCallback, useEffect, useState } from "react";
import * as css from "./mocks-tab.module.css";
import { RpcDetailEditor } from "./rpc-detail-editor";

function getRpcState() {
  const overrides = getMockOverrides();
  const called = new Set(getCalledRpcs());
  const known = new Set(getKnownRpcs());

  const overridden = Object.keys(overrides).sort();
  const inUse = Array.from(called)
    .filter((name) => !(name in overrides))
    .sort();
  const overriddenSet = new Set(overridden);
  const rest = Array.from(known)
    .filter((name) => !overriddenSet.has(name) && !called.has(name))
    .sort();

  return { overrides, overridden, inUse, rest };
}

interface RpcRowProps {
  name: string;
  isMocked: boolean;
  hasOverride: boolean;
  disabled?: boolean;
  onToggle: (name: string, v: boolean) => void;
  onDataSaved: () => void;
}

const RpcRow = memo(function RpcRow({
  name,
  isMocked,
  hasOverride,
  disabled,
  onToggle,
  onDataSaved,
}: RpcRowProps) {
  const [expanded, setExpanded] = useState(false);
  const hasCustomData = getMockResponse(name) !== undefined;

  const handleClose = useCallback(() => {
    setExpanded(false);
    onDataSaved();
  }, [onDataSaved]);

  return (
    <div className={css.rpcRowWrapper}>
      <div className={css.rpcRow}>
        <button
          className={classNames(
            css.rpcName,
            hasOverride && css.rpcNameOverridden,
            hasCustomData && css.rpcNameCustomData,
          )}
          onClick={() => setExpanded((v) => !v)}
          title="Click to view/edit mock response"
        >
          {name}
        </button>
        <RedoToggle
          disabled={disabled}
          setValue={(v) => onToggle(name, v)}
          size="xs"
          value={isMocked}
        />
      </div>
      {expanded && <RpcDetailEditor name={name} onClose={handleClose} />}
    </div>
  );
});

interface SectionProps {
  label: string;
  rpcs: string[];
  overrides: Record<string, boolean>;
  mockDefault: boolean;
  disabled?: boolean;
  onToggle: (name: string, v: boolean) => void;
  onDataSaved: () => void;
}

const RpcSection = memo(function RpcSection({
  label,
  rpcs,
  overrides,
  mockDefault,
  disabled,
  onToggle,
  onDataSaved,
}: SectionProps) {
  if (rpcs.length === 0) return null;
  return (
    <Flex dir="column" gap="none">
      <div className={css.sectionHeader}>
        {label}
        <span className={css.sectionCount}>{rpcs.length}</span>
      </div>
      {rpcs.map((name) => {
        const hasOverride = name in overrides;
        const isMocked = hasOverride ? (overrides[name] ?? false) : mockDefault;
        return (
          <RpcRow
            disabled={disabled}
            hasOverride={hasOverride}
            isMocked={isMocked}
            key={name}
            name={name}
            onDataSaved={onDataSaved}
            onToggle={onToggle}
          />
        );
      })}
    </Flex>
  );
});

interface RestEditorProps {
  pattern: string;
  onClose: () => void;
}

const RestEndpointEditor = memo(function RestEndpointEditor({
  pattern,
  onClose,
}: RestEditorProps) {
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState<"custom" | "default">("default");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    void resolveMockForRestEndpoint(pattern).then((value) => {
      setText(JSON.stringify(value, null, 2));
      setSource(
        getMockRestResponse(pattern) !== undefined ? "custom" : "default",
      );
      setLoading(false);
    });
  }, [pattern]);

  const handleSave = useCallback(() => {
    try {
      setMockRestResponse(pattern, JSON.parse(text));
      setSource("custom");
      setError(null);
      onClose();
    } catch {
      setError("Invalid JSON");
    }
  }, [pattern, text, onClose]);

  const handleReset = useCallback(() => {
    clearMockRestResponse(pattern);
    void resolveMockForRestEndpoint(pattern).then((value) => {
      setText(JSON.stringify(value, null, 2));
      setSource("default");
      setError(null);
    });
  }, [pattern]);

  if (loading)
    return (
      <div className={css.restEditor}>
        <span className={css.restEditorSource}>Loading…</span>
      </div>
    );

  return (
    <div className={css.restEditor}>
      <span className={css.restEditorSource}>Source: {source}</span>
      <textarea
        className={classNames(
          css.restEditorTextarea,
          error && css.restEditorTextareaError,
        )}
        onChange={(e) => {
          setText(e.target.value);
          setError(null);
        }}
        spellCheck={false}
        value={text}
      />
      {error && <span className={css.restEditorError}>{error}</span>}
      <div className={css.restEditorActions}>
        <RedoButton
          hierarchy="primary"
          onClick={handleSave}
          size="sm"
          text="Save"
        />
        <RedoButton
          hierarchy="secondary"
          onClick={handleReset}
          size="sm"
          text="Reset to default"
        />
        <RedoButton
          hierarchy="tertiary"
          onClick={onClose}
          size="sm"
          text="Cancel"
        />
      </div>
    </div>
  );
});

interface RestRowProps {
  method: string;
  pattern: string;
  hasCustomData: boolean;
  onSaved: () => void;
}

const RestEndpointRow = memo(function RestEndpointRow({
  method,
  pattern,
  hasCustomData,
  onSaved,
}: RestRowProps) {
  const [expanded, setExpanded] = useState(false);
  const handleClose = useCallback(() => {
    setExpanded(false);
    onSaved();
  }, [onSaved]);

  return (
    <div className={css.rpcRowWrapper}>
      <div className={css.rpcRow}>
        <button
          className={classNames(
            css.rpcName,
            hasCustomData && css.rpcNameCustomData,
          )}
          onClick={() => setExpanded((v) => !v)}
          title="Click to view/edit mock response"
        >
          <span className={css.restMethod}>{method}</span>
          {pattern}
        </button>
      </div>
      {expanded && (
        <RestEndpointEditor onClose={handleClose} pattern={pattern} />
      )}
    </div>
  );
});

export const MocksTab = memo(function MocksTab() {
  const [mockEnabled, setMockEnabledState] = useState(isMockModeEnabled);
  const [mockDefault, setMockDefaultState] = useState(getMockDefault);
  const [search, setSearch] = useState("");
  const [state, setState] = useState(getRpcState);

  useEffect(() => {
    const interval = setInterval(() => setState(getRpcState()), 1000);
    return () => clearInterval(interval);
  }, []);

  const handleGlobalToggle = useCallback((enabled: boolean) => {
    setMockModeEnabled(enabled);
    setMockEnabledState(enabled);
  }, []);

  const handleMockDefaultToggle = useCallback((enabled: boolean) => {
    setMockDefault(enabled);
    setMockDefaultState(enabled);
    setState(getRpcState());
  }, []);

  const handleRpcToggle = useCallback(
    (name: string, v: boolean) => {
      if (v === mockDefault) {
        clearMockOverride(name);
      } else {
        setMockOverride(name, v);
      }
      setState(getRpcState());
    },
    [mockDefault],
  );

  const handleReset = useCallback(() => {
    resetMockOverrides();
    setState(getRpcState());
  }, []);

  const handleDataSaved = useCallback(() => {
    setState(getRpcState());
  }, []);

  const { overrides, overridden, inUse, rest } = state;

  const filter = (names: string[]) =>
    search
      ? names.filter((n) => n.toLowerCase().includes(search.toLowerCase()))
      : names;

  const filteredOverridden = filter(overridden);
  const filteredInUse = filter(inUse);
  const filteredRest = search ? filter(rest) : [];
  const totalVisible =
    filteredOverridden.length + filteredInUse.length + filteredRest.length;

  return (
    <Flex dir="column" gap="md">
      <Flex align="center" gap="sm" justify="space-between">
        <RedoToggle
          label="Enable"
          setValue={handleGlobalToggle}
          size="sm"
          value={mockEnabled}
        />
        <Flex align="center" gap="sm">
          {mockEnabled && (
            <RedoButton
              hierarchy={mockDefault ? "primary" : "secondary"}
              onClick={() => handleMockDefaultToggle(!mockDefault)}
              size="sm"
              text="Mock all"
            />
          )}
          <RedoButton
            hierarchy="link"
            onClick={handleReset}
            size="sm"
            text="Reset"
          />
        </Flex>
      </Flex>

      <RedoTextInput
        placeholder="Search all RPCs..."
        setValue={setSearch}
        size="sm"
        value={search}
      />

      {totalVisible === 0 ? (
        <div className={css.emptyState}>
          {search ? "No RPCs match your search." : "No RPCs recorded yet."}
        </div>
      ) : (
        <Flex dir="column" gap="lg" pt="md">
          <RpcSection
            disabled={!mockEnabled}
            label="Overridden"
            mockDefault={mockDefault}
            onDataSaved={handleDataSaved}
            onToggle={handleRpcToggle}
            overrides={overrides}
            rpcs={filteredOverridden}
          />
          <RpcSection
            disabled={!mockEnabled}
            label="In use"
            mockDefault={mockDefault}
            onDataSaved={handleDataSaved}
            onToggle={handleRpcToggle}
            overrides={overrides}
            rpcs={filteredInUse}
          />
          {filteredRest.length > 0 && (
            <RpcSection
              disabled={!mockEnabled}
              label="All RPCs"
              mockDefault={mockDefault}
              onDataSaved={handleDataSaved}
              onToggle={handleRpcToggle}
              overrides={overrides}
              rpcs={filteredRest}
            />
          )}
        </Flex>
      )}

      {!search && rest.length > 0 && (
        <Text fontSize="xxs" textColor="tertiary">
          Search to browse {rest.length} more RPCs
        </Text>
      )}

      <RestSection onSaved={handleDataSaved} />
    </Flex>
  );
});

const RestSection = memo(function RestSection({
  onSaved,
}: {
  onSaved: () => void;
}) {
  const [, forceUpdate] = useState(0);
  const refresh = useCallback(() => {
    forceUpdate((n) => n + 1);
    onSaved();
  }, [onSaved]);

  const endpoints = getMockRestRegistry();
  if (endpoints.length === 0) return null;

  return (
    <Flex dir="column" gap="none">
      <div className={css.sectionHeader}>
        REST Endpoints
        <span className={css.sectionCount}>{endpoints.length}</span>
      </div>
      {endpoints.map((ep) => (
        <RestEndpointRow
          hasCustomData={getMockRestResponse(ep.pattern) !== undefined}
          key={`${ep.method ?? "GET"}:${ep.pattern}`}
          method={ep.method ?? "GET"}
          onSaved={refresh}
          pattern={ep.pattern}
        />
      ))}
    </Flex>
  );
});
